export function transformGlQbRows(data) {
  if (!data?.Rows?.Row) return data;

  /* =====================================================
     SAFE DEEP CLONE
  ===================================================== */
  const deepClone = obj => {
    if (typeof structuredClone === "function") {
      try {
        return structuredClone(obj);
      } catch {}
    }
    return JSON.parse(JSON.stringify(obj));
  };

  const clone = deepClone(data);

  /* =====================================================
     HELPERS
  ===================================================== */

  const removeAccountNumber = text =>
    typeof text === "string"
      ? text.replace(/^\d+(?:\.\d+)*\s+/, "").trim()
      : text;

  const getAccountNumber = text => {
    if (typeof text !== "string") return null;
    const match = text.trim().match(/^(\d+(?:\.\d+)*)\s+/);
    return match ? match[1] : null;
  };

  const getHeaderText = section =>
    section?.Header?.ColData?.[0]?.value
      ? removeAccountNumber(section.Header.ColData[0].value)
      : "";

  const getHeaderSegments = section => {
    const txt = getHeaderText(section);
    return txt ? txt.split(":").map(s => s.trim()).filter(Boolean) : [];
  };

  const hasHeaderValue = section =>
    !!section?.Header?.ColData?.some(col => col?.value);

  const isSectionObject = r =>
    !!(
      r &&
      typeof r === "object" &&
      !r?.ColData &&
      (r?.type === "Section" ||
        Array.isArray(r?.Rows?.Row) ||
        r?.Header?.ColData)
    );

  const isWrapperSection = section =>
    isSectionObject(section) &&
    Array.isArray(section?.Rows?.Row) &&
    !hasHeaderValue(section);

  /* =====================================================
     RENAME MOVED SECTION WITH FULL PATH
  ===================================================== */
  const renameMovedSection = (section, parentPathSegments) => {
    const copy = deepClone(section);

    const headerValue = copy?.Header?.ColData?.[0]?.value;
    if (!headerValue) return { section: copy, path: parentPathSegments };

    const accountNumber = getAccountNumber(headerValue);
    const cleanHeader = removeAccountNumber(headerValue);

    const childSegments = cleanHeader
      .split(":")
      .map(s => s.trim())
      .filter(Boolean);

    const fullPathSegments = [...parentPathSegments, ...childSegments];
    const fullPath = fullPathSegments.join(":");

    copy.Header.ColData[0].value = accountNumber
      ? `${accountNumber} ${fullPath}`
      : fullPath;

    // IMPORTANT → mark as already hierarchy-expanded
    copy.__isMoved = true;

    return { section: copy, path: fullPathSegments };
  };

  /* =====================================================
     CORE RECURSION
  ===================================================== */
  const processSections = (sections, parentPath = []) => {
    if (!Array.isArray(sections)) return [];

    const result = [];

    for (const section of sections) {
      const rows = Array.isArray(section?.Rows?.Row)
        ? section.Rows.Row
        : null;

      if (!rows) {
        result.push(section);
        continue;
      }

      /* ---------------------------------------------
         BUILD STRUCTURAL PATH
      --------------------------------------------- */

      const currentSegments = getHeaderSegments(section);

      // If already moved, header already contains full path
      const fullParentPath = section.__isMoved
        ? parentPath
        : [...parentPath, ...currentSegments];

      /* ---------------------------------------------
         UNWRAP WRAPPERS
      --------------------------------------------- */
      const wrappers = rows.filter(isWrapperSection);

      if (wrappers.length) {
        const unwrappedRows = [];
        const nonWrappers = [];

        for (const r of rows) {
          if (isWrapperSection(r) && Array.isArray(r?.Rows?.Row)) {
            unwrappedRows.push(...r.Rows.Row);
          } else {
            nonWrappers.push(r);
          }
        }

        section.Rows.Row = processSections(unwrappedRows, fullParentPath);
        result.push(section);

        for (const r of nonWrappers) {
          const { section: moved, path } = renameMovedSection(
            r,
            fullParentPath
          );
          result.push(...processSections([moved], path));
        }

        continue;
      }

      /* ---------------------------------------------
         ALL CHILDREN ARE SECTIONS → MOVE ALL OUT
      --------------------------------------------- */
      const allChildrenAreSections =
        rows.length > 0 && rows.every(isSectionObject);

      if (allChildrenAreSections) {
        for (const r of rows) {
          const { section: moved, path } = renameMovedSection(
            r,
            fullParentPath
          );
          result.push(...processSections([moved], path));
        }
        continue;
      }

      /* ---------------------------------------------
         MIXED CONTENT (data + sections)
      --------------------------------------------- */
      if (hasHeaderValue(section)) {
        const remaining = [];
        const moved = [];

        for (const r of rows) {
          if (isSectionObject(r)) {
            const { section: movedSection, path } = renameMovedSection(
              r,
              fullParentPath
            );
            moved.push(...processSections([movedSection], path));
          } else {
            remaining.push(r);
          }
        }

        if (remaining.length) {
          section.Rows.Row = processSections(remaining, fullParentPath);
        } else {
          delete section.Rows;
        }

        result.push(section);
        result.push(...moved);
        continue;
      }

      /* ---------------------------------------------
         DEFAULT RECURSION
      --------------------------------------------- */
      section.Rows.Row = processSections(rows, fullParentPath);
      result.push(section);
    }

    return result;
  };

  clone.Rows.Row = processSections(clone.Rows.Row, []);
  return clone;
}